const timestamp = value => {
  const parsed = value ? new Date(value).getTime() : 0;
  return Number.isFinite(parsed) ? parsed : 0;
};

const KIND_ORDER = { activity_event: 0, insight: 1, proposal: 2, message: 3 };

const PROPOSAL_ACTIONABLE_STATUSES = ['pending'];

// A proposal's chronological anchor is when it last changed state: created for a
// live pending proposal, resolved (updated) for an approved/declined record.
const proposalTimestamp = proposal => (
  proposal?.status && proposal.status !== 'pending'
    ? (proposal?.updated_at || proposal?.resolved_at || proposal?.created_at)
    : (proposal?.created_at || proposal?.updated_at)
);


export const getActivityEventPresentation = event => {
  const dayClosing = event?.event_type === 'day_closed';
  const completed = event?.event_type === 'activity_completed' || dayClosing;
  const reopened = event?.event_type === 'activity_reopened';
  const reactionOnlyComplete = event?.reply_worthwhile === false && Boolean(event?.coach_reaction);
  const coachReplyFailed = event?.generation_state === 'failed' && !reactionOnlyComplete;
  const selectedDayClosure = dayClosing && (event?.routing === 'external' || Boolean(event?.external_task_id || event?.external_task_unavailable));
  const selectedMeal = event?.activity_type === 'meal' && (event?.routing === 'external' || Boolean(event?.external_task_id || event?.external_task_unavailable));
  return {
    completed,
    ...(dayClosing ? { dayClosing: true } : {}),
    ...(reopened ? { reopened: true } : {}),
    side: completed || reopened ? 'user' : 'coach',
    showCoachAvatar: !(completed || reopened),
    icon: completed ? '✓' : (reopened ? '↻' : '⌛'),
    label: dayClosing ? 'Meals complete' : (completed ? 'Completed' : (reopened ? 'Reopened' : 'Expired')),
    awaitingCoachReply: ['pending', 'claimed'].includes(event?.generation_state)
      && event?.reply_worthwhile !== false,
    coachReplyFailed,
    canRetryReview: coachReplyFailed && (event?.retryable === undefined ? !(selectedMeal || selectedDayClosure) : event.retryable === true),
    ...(selectedMeal ? { selectedMeal: true } : {}),
    ...(selectedDayClosure ? { selectedDayClosure: true } : {}),
    reaction: event?.coach_reaction || null,
    replyWorthwhile: event?.reply_worthwhile !== false,
  };
};

export const getActivityReviewStatusText = (event, coachName = 'Coach') => (
  event?.generation_state === 'pending'
    ? `${coachName} queued this review`
    : `${coachName} is reviewing this`
);

export const getInsightTeaser = insight => {
  if (insight?.kind === 'activity_update') {
    return insight.general_advice
      || insight.strategy_assessment?.status_summary
      || 'Your coach reviewed this activity';
  }
  return insight?.general_advice
    || insight?.strategy_assessment?.status_summary
    || 'Tap to read your insight for the day';
};

export const getInsightPresentation = insight => {
  const isActivityReply = insight?.kind === 'activity_update';
  return {
    isActivityReply,
    showCoachAvatar: isActivityReply,
    side: isActivityReply ? 'coach' : 'insight',
  };
};

export const coachInsightHasVisibleContent = (insight, actions = []) => {
  const hasText = Boolean(
    String(insight?.general_advice || '').trim()
    || String(insight?.strategy_assessment?.status_summary || '').trim()
  );
  const hasRecommendations = ['workout_recommendations', 'meal_recommendations', 'media_follow_up_requests', 'ai_actions_applied']
    .some(field => Array.isArray(insight?.[field]) && insight[field].length > 0);
  const hasAction = Boolean(
    (Array.isArray(actions) && actions.length > 0)
    || insight?.pending_exercise_adjustment_proposal
    || insight?.pending_media_requirements_proposal
    || insight?.pending_nutrition_target_proposal
    || insight?.pending_metric_cadence_proposal
    || insight?.media_requirement_update
  );
  return hasText || hasRecommendations || hasAction;
};

const isReactionOnlyInsight = (insight, actions = []) => {
  if (insight?.kind !== 'activity_update' || insight?.activity_feedback?.reply_worthwhile !== false) return false;
  return !coachInsightHasVisibleContent(insight, actions);
};

export const buildCoachTimeline = ({ conversation = [], insights = [], activityEvents = [], proposals = [] } = {}) => {
  const timeline = conversation.filter(msg => msg?.message_type !== 'manager_report').map((msg, index) => ({
    kind: 'message',
    key: `msg-${msg?._id || `${timestamp(msg?.created_at)}-${index}`}`,
    ts: timestamp(msg?.created_at),
    msg,
  }));

  for (const proposal of proposals) {
    const status = proposal?.status || 'pending';
    timeline.push({
      kind: 'proposal',
      key: `proposal-${proposal?.id || proposal?._id}`,
      id: String(proposal?.id || proposal?._id || ''),
      ts: timestamp(proposalTimestamp(proposal)),
      proposal,
      status,
      actionable: PROPOSAL_ACTIONABLE_STATUSES.includes(status),
    });
  }

  for (const item of insights) {
    const insight = item?.insight || item;
    if (isReactionOnlyInsight(insight, item?.actions || [])) {
      continue;
    }
    timeline.push({
      kind: 'insight',
      key: `insight-${insight?._id}`,
      id: String(insight?._id || ''),
      ts: timestamp(insight?.created_at),
      insight,
      actions: item?.actions || [],
      replyToEventIds: Array.isArray(insight?.trigger?.event_ids)
        ? insight.trigger.event_ids.map(String)
        : [],
    });
  }

  for (const event of activityEvents) {
    timeline.push({
      kind: 'activity_event',
      key: `activity-event-${event?._id || event?.event_key}`,
      id: String(event?._id || event?.event_key || ''),
      ts: timestamp(event?.occurred_at || event?.created_at),
      event,
    });
  }

  timeline.sort((a, b) => (
    a.ts - b.ts
    || KIND_ORDER[a.kind] - KIND_ORDER[b.kind]
    || a.key.localeCompare(b.key)
  ));
  // Keep each generated activity reply conversationally attached to the
  // lifecycle event it answers, even when unrelated chat arrived while the
  // asynchronous review was running.
  const activityReplies = timeline.filter(item => (
    item.kind === 'insight'
    && item.insight?.kind === 'activity_update'
    && item.replyToEventIds.length > 0
  ));
  for (const reply of activityReplies) {
    const currentIndex = timeline.indexOf(reply);
    const eventIndex = timeline.findIndex(item => (
      item.kind === 'activity_event' && reply.replyToEventIds.includes(item.id)
    ));
    if (currentIndex < 0 || eventIndex < 0 || currentIndex === eventIndex + 1) continue;
    timeline.splice(currentIndex, 1);
    const refreshedEventIndex = timeline.findIndex(item => item.kind === 'activity_event' && reply.replyToEventIds.includes(item.id));
    timeline.splice(refreshedEventIndex + 1, 0, reply);
  }
  return timeline;
};
